// ---------------------------------------------------------------------------
// Layouts de desktop por página — a PORTA do app para `src/desktop/pages/`.
//
// Mesmo desenho da casca (`AppFrame`): o teste de `VITE_NATIVE_BUILD` é escrito
// AQUI, no próprio módulo, para o Rollup dobrar a constante e descartar os
// `import()` na build das lojas — e o `mobile-postbuild.mjs` falha se algum
// fonte de `src/desktop/` sobrar. Na web, cada layout é um chunk próprio,
// buscado só quando a casca está ligada e a página abre.
//
// A página continua dona dos dados e das ações; o layout só recebe as peças.
//
// ⚠️ Convenção de nome: o arquivo de entrada é `<Página>Desktop.tsx`. É o que o
// `globIgnores` do `vite.config.ts` usa para tirar o chunk do precache da PWA
// (senão o celular o baixaria na instalação).
// ---------------------------------------------------------------------------

import { lazy } from "react";

const IS_NATIVE_BUILD = import.meta.env.VITE_NATIVE_BUILD === "true";

export const HomeDesktopLayout = IS_NATIVE_BUILD
  ? null
  : lazy(() => import("../../desktop/pages/home/HomeDesktop"));

export const BookDesktopLayout = IS_NATIVE_BUILD
  ? null
  : lazy(() => import("../../desktop/pages/book/BookDesktop"));

export const ProfileDesktopLayout = IS_NATIVE_BUILD
  ? null
  : lazy(() => import("../../desktop/pages/profile/ProfileDesktop"));

/** O provador da Loja ("Como fica em você") — ver `StoreShopTab`. */
export const StoreTryOnLayout = IS_NATIVE_BUILD
  ? null
  : lazy(() => import("../../desktop/pages/store/StoreTryOnDesktop"));

/**
 * Duas colunas genéricas (conteúdo + painel) das telas da leva 2 do mock —
 * ver `desktop/pages/split/SplitDesktop.tsx`. Envolva em `<Suspense>`.
 */
export const SplitDesktopLayout = IS_NATIVE_BUILD
  ? null
  : lazy(() => import("../../desktop/pages/split/SplitDesktop"));

/** "Lendo agora" público (`/lendo/:username`) — ver `desktop/pages/reading/`. */
export const ReadingDesktopLayout = IS_NATIVE_BUILD
  ? null
  : lazy(() => import("../../desktop/pages/reading/ReadingDesktop"));

/**
 * Pré-carga dos layouts de desktop, chamada pelo `AppFrame` assim que a casca
 * liga (05/10). Os chunks `*Desktop-*` ficam fora do precache da PWA, então a
 * 1ª ida a Início, Perfil ou Livro depois de cada deploy esperava a rede com a
 * área vazia ao lado da barra lateral (medido: 444–522 ms com CPU 4×). Mesmo
 * gate de build dos `lazy()` acima: no app das lojas a função nem existe.
 */
export const preloadDesktopLayouts: (() => void) | null = IS_NATIVE_BUILD
  ? null
  : () => {
      // Mesmo corte do `routePrefetch.ts`: nos testes a pré-carga só arrasta
      // os 4 layouts (e as dependências) para todo teste que liga a casca.
      if (import.meta.env.MODE === "test") return;
      for (const load of [
        () => import("../../desktop/pages/home/HomeDesktop"),
        () => import("../../desktop/pages/profile/ProfileDesktop"),
        () => import("../../desktop/pages/book/BookDesktop"),
        () => import("../../desktop/pages/split/SplitDesktop"),
      ]) {
        // Falhou (rede, deploy no meio): o `lazy()` da página tenta de novo.
        void load().catch(() => {});
      }
    };
